import { Injectable} from '@angular/core';
import { MapInitOptions } from '../interfaces';
import { MapEventsController } from './map-events.controller';
import { MapLayersController } from './map-layers.controller';
import { MapDrawingController } from './draw/map-drawing.controller';
import { MapZoomRegionController } from './map-zoom-region.controller';
import { environment } from '@root/src/environments/environment';
import {TranslateService} from '@ngx-translate/core';
import {DEFAULT_MARKER_CLUSTER_OPTIONS} from '../constants/marker-cluster.constants';


@Injectable({
  providedIn: 'root'
})
export class OpenStreetMapController {
  private mapInstance: any;
  private eventsController: MapEventsController;
  private layersController!: MapLayersController;
  private drawingController!: MapDrawingController;
  private zoomRegionController?: MapZoomRegionController;

  private translateService ;

  constructor(translateService:TranslateService) {
    this.translateService = translateService;
    this.eventsController = new MapEventsController();
  }

  /**
   * Initialize the map with options
   */
  initialize(mapElement: HTMLElement, options: MapInitOptions): void {
    if (typeof (window as any).L === 'undefined') {
      console.error('Leaflet library is not loaded');
      return;
    }

    // Extended bounds to show legends at the bottom - extended south from 37.0 to 35.0
    // const uzbekistanBounds = (window as any).L.latLngBounds([35.0, 55.5], [46.0, 74.0]);

    const mapOptions: any = {
      zoomControl: false,
      minZoom: 1,
      maxZoom: 18,
      fadeAnimation: true,
      zoomAnimation: true
    };

    if (!options.disableBounds) {
      // mapOptions.maxBounds = uzbekistanBounds;
      // mapOptions.maxBoundsViscosity = 1.0;
    }

    this.mapInstance = (window as any).L.map(mapElement, mapOptions).setView(
      options.center || [environment.map.lat, environment.map.lng],
      options.zoom || 13
    );


    // Get default layer from environment or use satellite
    const defaultLayer = environment.defaultMapLayer || 'Plan';
    // @ts-ignore
    const layerConfig = environment.mapLayers?.[defaultLayer] || environment.mapLayers?.plan;

    // Add default tile layer from environment
    const tileLayerOptions: any = {
      attribution: layerConfig.attribution,
      maxZoom: 18,
      minZoom: 1,
      noWrap: false,
      // bounds: uzbekistanBounds
    };

    // Add subdomains if available (for satellite layer)
    if (layerConfig.subdomains) {
      tileLayerOptions.subdomains = layerConfig.subdomains;
    }

    const defaultTileLayer = (window as any).L.tileLayer(
      layerConfig.url,
      tileLayerOptions
    );

    defaultTileLayer.addTo(this.mapInstance);

    this.layersController = new MapLayersController(this.mapInstance);
    this.drawingController = new MapDrawingController(this.mapInstance, this.translateService);

    this.setupLayers();
    this.setupEventListeners();
  }

  /**
   * Internal: Setup map layers

      // Update circle markers visibility based on zoom level
      this.layersController.updateAllCircleMarkersVisibilityByZoom();
   */
  private setupLayers(): void {
    if (!this.mapInstance.getPane('markerPane')) {
      const markerPane = this.mapInstance.createPane('markerPane');
      markerPane.style.zIndex = 950;
    }

    const markersLayer = (window as any).L.layerGroup({ pane: 'markerPane' }).addTo(this.mapInstance);
    this.layersController.setMarkersLayer(markersLayer);

    if (typeof (window as any).L.markerClusterGroup !== 'undefined') {
      const markerClusterGroup = (window as any).L.markerClusterGroup({
        ...DEFAULT_MARKER_CLUSTER_OPTIONS,
        pane: 'markerPane'
      });
      this.mapInstance.addLayer(markerClusterGroup);
      this.layersController.setMarkerClusterGroup(markerClusterGroup);
    }

    if (!this.mapInstance.getPane('dynamicPolygonsPane')) {
      const polygonsPane = this.mapInstance.createPane('dynamicPolygonsPane');
      polygonsPane.style.zIndex = 900;
    }

    const dynamicPolygonsLayer = (window as any).L.layerGroup({ pane: 'dynamicPolygonsPane' }).addTo(
      this.mapInstance
    );
    this.layersController.setDynamicPolygonsLayer(dynamicPolygonsLayer);

    const drawnItems = (window as any).L.featureGroup().addTo(this.mapInstance);
    this.drawingController.setDrawnItems(drawnItems);
  }

  /**
   * Clicks on markercluster bubble / marker / spiderfy leg must not emit a bare “map background” click
   * (otherwise Angular `mapClick` handlers fire alongside cluster zoom → unstable UI).
   */
  private static isMarkerClusterOrMarkerUiClick(e: any): boolean {
    const el = e?.originalEvent?.target as HTMLElement | undefined | null;
    if (!el?.closest) {
      return false;
    }
    return Boolean(
      el.closest('.marker-cluster') ||
      el.closest('.leaflet-marker-pane') ||
      el.closest('.leaflet-cluster-spider-leg')
    );
  }

  /**
   * Internal: Setup event listeners
   */
  private setupEventListeners(): void {
    this.mapInstance.on('click', (e: any) => {
      if (OpenStreetMapController.isMarkerClusterOrMarkerUiClick(e)) {
        return;
      }
      const coords = e.latlng;
      this.eventsController.emitMapClick({
        lat: coords.lat,
        lng: coords.lng,
        ctrlKey: e.originalEvent?.ctrlKey || false
      });
      this.eventsController.emitCoordinatesChange([coords.lat, coords.lng]);
    });

    this.mapInstance.on('contextmenu', (e: any) => {
      const coords = e.latlng;
      (window as any).L.DomEvent.preventDefault(e);
      this.eventsController.emitMapRightClick({
        lat: coords.lat,
        lng: coords.lng,
        originalEvent: e.originalEvent,
        containerPoint: e.containerPoint,
        clientX: e.originalEvent?.clientX,
        clientY: e.originalEvent?.clientY
      });
    });

    this.mapInstance.on('dblclick', (e: any) => {
      if (!this.drawingController.isRectangleDrawingActive() && !this.drawingController.isPolylineDrawingActive()) {
        const coords = e.latlng;
        this.eventsController.emitMapDblClick({
          lat: coords.lat,
          lng: coords.lng
        });
      }
    });

    this.mapInstance.on('moveend', () => {
      const bounds = this.mapInstance.getBounds();
      const center = this.mapInstance.getCenter();
      this.eventsController.emitMapMove({
        bounds,
        center: { lat: center.lat, lng: center.lng },
        zoom: this.mapInstance.getZoom()
      });
    });

    this.mapInstance.on('dragend', () => {
      const bounds = this.mapInstance.getBounds();
      const center = this.mapInstance.getCenter();
      this.eventsController.emitMapDragEnd({
        bounds,
        center: { lat: center.lat, lng: center.lng },
        zoom: this.mapInstance.getZoom()
      });
    });

    this.mapInstance.on('zoomend', () => {
      this.eventsController.emitMapZoomChange(this.mapInstance.getZoom());

      try {
        // Update polygon visibility based on zoom level
        this.layersController.updateAllPolygonsVisibilityByZoom();

        // Update circle markers visibility based on zoom level
        this.layersController.updateAllCircleMarkersVisibilityByZoom();
      } catch (e) {
        console.warn('[OpenStreetMap] zoomend layer updates failed', e);
      }
    });

    this.mapInstance.on('mousemove', (e: any) => {
      this.eventsController.emitMapMouseMove({
        lat: e.latlng.lat,
        lng: e.latlng.lng
      });
    });
  }

  // =========================================================================
  // PUBLIC API - CONTROLLER GETTERS
  // =========================================================================

  /**
   * Get the events controller
   */
  getEventsController(): MapEventsController {
    return this.eventsController;
  }

  /**
   * Get the layers controller
   */
  getLayersController(): MapLayersController {
    return this.layersController;
  }

  /**
   * Get the drawing controller
   */
  getDrawingController(): MapDrawingController {
    return this.drawingController;
  }

  /**
   * Get the zoom region controller
   */
  getZoomRegionController(): MapZoomRegionController | undefined {
    return this.zoomRegionController;
  }

  /**
   * Initialize hierarchical zoom-based region loading
   */
  initializeZoomRegionController(): MapZoomRegionController {
    if (!this.zoomRegionController) {
      this.zoomRegionController = new MapZoomRegionController(
        this.mapInstance,
        this.layersController,
        this.eventsController
      );
      this.zoomRegionController.initialize();
    }
    return this.zoomRegionController;
  }

  /**
   * Get the map instance
   */
  getMapInstance(): any {
    return this.mapInstance;
  }

  // =========================================================================
  // PUBLIC API - MAP OPERATIONS
  // =========================================================================

  /**
   * Set map center and zoom
   */
  setCenter(lat: number, lng: number, zoom?: number): void {
    if (this.mapInstance) {
      this.mapInstance.setView([lat, lng], zoom || 13);
    }
  }

  /**
   * Get current map center
   */
  getCenter(): { lat: number; lng: number } | null {
    if (!this.mapInstance) return null;
    try {
      const center = this.mapInstance.getCenter();
      return { lat: center.lat, lng: center.lng };
    } catch (error) {
      return null;
    }
  }

  /**
   * Get current map zoom level
   */
  getZoom(): number | undefined {
    if (!this.mapInstance) return undefined;
    return this.mapInstance.getZoom();
  }

  /**
   * Remove/destroy the map
   */
  removeMap(): void {
    if (this.mapInstance) {
      this.mapInstance.remove();
    }
  }
}
